import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SharedLayout from "../../layouts/shared-layout/SharedLayout";
import PaginatedTable, {
  TableColumn,
} from "../../shared/components/PaginatedTable";
import { useClient } from "../../context/ClientContext";
import { fetchData } from "../../services/FetchDataService";
import { useNotification } from "../../context/NotificationContext";
import { useDialog } from "../../context/DialogContext";
import { ClinicalTrialFilter } from "../../shared/interfaces/Filters";

const ClinicalTrialList: React.FC = () => {
  const [clinicalTrials, setClinicalTrials] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const [pagination, setPagination] = useState({
    page: 1,
    last_page: 0,
    total_items: 0,
    page_size: 10,
    is_first_page: true,
    is_last_page: false,
  });

  const userID = localStorage.getItem("userID") ?? "";
  const [filter, setFilter] = useState<ClinicalTrialFilter>({
    page: 1,
    page_size: 10,
    researcher_id: parseInt(userID, 10),
  });

  const { success, error } = useNotification();
  const { openDialog } = useDialog();
  const client = useClient("CLINICAL_TRIAL_SERVICE");

  // NEW: For navigation
  const navigate = useNavigate();

  const handleAddItem = () => {
    navigate("/clinical-trial/create");
  };

  const handleEdit = (row: any) => {
    navigate(`/clinical-trial/edit/${row.id}`);
  };

  const handleView = (row: any) => {
    navigate(`/clinical-trial/view/${row.id}`);
  };

  const handleDelete = async (id: string) => {
    try {
      const payload = {
        id: parseInt(id, 10),
        deleted_by: 0,
      };
      payload.deleted_by = 1;
      const response = await client.post("/clinical-trial/delete", payload);

      if (response.data) {
        // Update your local state if needed.
        setClinicalTrials((prev) =>
          prev.filter((item) => item.id !== parseInt(id, 10))
        );
      }
    } catch (er) {
      error("Error while deleting clinical trial");
      console.error("Error deleting clinical trial:", er);
    } finally {
      fetchClinicalTrialList();
    }
  };

  const fetchClinicalTrialList = async () => {
    setLoading(true);
    try {
      const response = await fetchData(client, "/clinical-trial/list", filter);
      const { data, pagination } = response;

      setClinicalTrials(data || []);
      setPagination((prev) => ({
        ...prev,
        page: pagination?.page ?? 1,
        last_page: pagination?.last_page ?? 0,
        total_items: pagination?.total_items ?? 0,
        page_size: pagination?.page_size ?? 10,
        is_first_page: pagination?.page === 1,
        is_last_page: pagination?.page === pagination?.last_page,
      }));
    } catch (error) {
      console.error("Error fetching clinical trials:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClinicalTrialList();
  }, [filter]);

  const columns: TableColumn[] = [
    { header: "Scientific Title", accessor: "scientific_title" },
    { header: "Public Title", accessor: "public_title" },
    { header: "Scientific Acronym", accessor: "scientific_acronym" },
    { header: "Protocol Number", accessor: "protocol_number" },
    { header: "Protocol Version", accessor: "protocol_version" },
    {
      header: "Action",
      isAction: true,
      actionButtons: [
        {
          id: "view",
          label: "View",
          icon: "visibility",
          class: "text-tertiary",
          permissions: ["/auth/login"],
          action: (row) => handleView(row),
        },
        {
          id: "edit",
          label: "Edit",
          icon: "create",
          class: "text-primary",
          permissions: ["/auth/login"],
          action: (row) => handleEdit(row),
        },
        {
          id: "delete",
          label: "Delete",
          icon: "delete_sweep",
          class: "text-danger",
          permissions: ["/auth/login"],
          action: (row) => handleDelete(row.id),
        },
      ],
    },
  ];

  return (
    <SharedLayout
      formTitle="Clinical Trials"
      onAddItem={handleAddItem}
      animationSize="eighty"
      addPermission={["/auth/login"]}
    >
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <span className="text-lg font-semibold text-gray-500">
            Loading clinical trials...
          </span>
        </div>
      ) : (
        <PaginatedTable
          columns={columns}
          data={clinicalTrials}
          loading={loading}
          pagination={{
            page: pagination.page,
            page_size: pagination.page_size,
            last_page: pagination.last_page,
            total_items: pagination.total_items,
            is_first_page: pagination.is_first_page,
            is_last_page: pagination.is_last_page,
            onPageChange: (p) => setFilter({ ...filter, page: p }),
            onPageSizeChange: (size) =>
              setFilter({ ...filter, page_size: size, page: 1 }),
          }}
          searchFields={[
            "scientific_title",
            "public_title",
            "protocol_number",
            "protocol_version",
          ]}
          onSearch={(field, query) => {
            setFilter({
              ...filter,
              page: 1,
              [field]: query,
            });
          }}
        />
      )}
    </SharedLayout>
  );
};

export default ClinicalTrialList;
